// @/utils/designCacheDB.js
import _ from "lodash"
import { Notification } from 'element-ui'
import { uploadFileNew } from "@/views/customProduct/online/uploadNew.js"
import { addUserDesign } from "@/api/customProduct/design"
import store from "@/store"
class DesignCacheDB {
  constructor() {
    this.dbName = this.getDBName();
    this.storeName = 'designCaches';
    this.db = null;
    this.listeners = []; // 监听器数组
    this.isMonitoring = false; // 是否正在监听
    this.uploadHandler = null; // 上传处理函数
    this.aliyunOssToken = null; // oss
    this.currUserName = null; //当前账户（在store\modules\user.js设置）
    this.uploadNotify = {}; //通知
  }

  //数据隔离
  getDBName() {
    // if (typeof document !== 'undefined' && document.location) { //由于同源策略，浏览器本身会数据隔离，所以其实没必要设置名称隔离
    //   // 将 host 中的点、冒号等替换为下划线，避免数据库名非法字符
    //   const host = document.location.host.replace(/[.:]/g, '_');
    //   return `DesignCacheDB_${host}`;
    // }
    return 'DesignCacheDB';
  }
  
  // 初始化数据库
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          // 创建索引便于查询
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('status', 'status', { unique: false });  //pending, uploading, uploaded
          store.createIndex('userName', 'userName', { unique: false });
        }
      };
      
      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }
  
  // 获取所有设计缓存
  async getAllDesigns() {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();
      
      request.onsuccess = () => {
        resolve(request.result || []);
      };
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }
  
  // 添加新设计
  async addDesign(designData) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.add(designData);
      
      request.onsuccess = () => {
        resolve(request.result);
        // 触发监听器
        this.notifyListeners('add', designData);
      };
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }
  
  // 更新设计状态
  async updateDesignStatus(id, status) {
    await this.init();
    
    return new Promise(async (resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      // 先获取现有数据
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const design = getRequest.result;
        if (design) {
          design.status = status;
          design.updatedAt = Date.now();
          
          const updateRequest = store.put(design);
          updateRequest.onsuccess = () => {
            resolve();
            // 触发监听器
            this.notifyListeners('update', design);
          };
          updateRequest.onerror = (event) => reject(event.target.error);
        } else {
          reject(new Error('Design not found'));
        }
      };
      
      getRequest.onerror = (event) => reject(event.target.error);
    });
  }
  
  // 删除设计
  async deleteDesign(id) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        resolve();
        // 触发监听器
        this.notifyListeners('delete', { id });
      };
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }
  
  // 清空所有设计缓存
  async clearAll() {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();
      
      request.onsuccess = () => {
        resolve();
        // 触发监听器
        this.notifyListeners('clear', {});
      };
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }
  
  // 获取特定状态的设计
  async getDesignsByStatus(status) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('status');
      const request = index.getAll(status);
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = (event) => reject(event.target.error);
    });
  }
  // 获取特定用户的设计
  async getDesignsByUserName(userName) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('userName');
      const request = index.getAll(userName);
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = (event) => reject(event.target.error);
    });
  }
  
  // 获取按时间排序的设计
  async getDesignsSortedByTime(limit = 50) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      
      // 降序排列（最新的在前面）
      const request = index.openCursor(null, 'prev');
      const results = [];
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor && results.length < limit) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      
      request.onerror = (event) => reject(event.target.error);
    });
  }
  
  // ================ 新增：全局监听功能 ================
  
  // 添加监听器
  addListener(listener) {
    this.listeners.push(listener);
  }
  
  // 移除监听器
  removeListener(listener) {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }
  
  // 通知所有监听器
  notifyListeners(eventType, data) {
    this.listeners.forEach(listener => {
      try {
        listener(eventType, data);
      } catch (error) {
        console.error('监听器执行出错:', error);
      }
    });
  }
  
  // 设置上传处理函数
  setUploadHandler(handler) {
    this.uploadHandler = handler;
  }
  
  // 开始全局监听
  async startMonitoring() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    
    // 监听数据库变化
    this.addListener(async (eventType, data) => {
      if (eventType === 'add') {
        // 新增数据时触发上传检查
        await this.checkAndUploadDesigns('add', data);
      }
    });
    
    // 初始检查一次
    await this.checkAndUploadDesigns();
    
    // console.log('设计缓存监听已启动');
  }
  
  // 停止监听
  stopMonitoring() {
    this.isMonitoring = false;
    this.listeners = [];
    // console.log('设计缓存监听已停止');
  }
  
  // 检查并上传设计
  async checkAndUploadDesigns(type, data) {
    try {
      // 获取所有待上传的设计
      let unsentDesigns = []
      if (type == 'add') {
        unsentDesigns = [JSON.parse(JSON.stringify(data))] //当前操作的设计
      } else { //页面首次加载上传只会触发一次
        const pendingDesigns = await this.getDesignsByUserName(this.currUserName);
        unsentDesigns = pendingDesigns.filter(design => ['pending', 'uploading'].includes(design.status));
      }

      // console.log('unsentDesigns: ',unsentDesigns)
      
      if (unsentDesigns.length > 0) {
        // console.log(`发现 ${unsentDesigns.length} 个待上传的设计`);
        
        // 如果有设置上传处理函数，则调用
        if (this.uploadHandler && typeof this.uploadHandler === 'function') {
          for (const design of unsentDesigns) {
            try {
              // 调用上传处理函数
              await this.updateDesignStatus(design.id, 'uploading');
              const success = await this.uploadHandler(design, type);
              
              if (success) {
                // 上传成功后更新状态或删除
                await this.updateDesignStatus(design.id, 'uploaded');
                await this.deleteDesign(design.id);
                // console.log(`设计 ${design.id} 上传成功`);
              } else {
                // await this.updateDesignStatus(design.id, 'pending');
                console.warn(`设计 ${design.id} 上传失败`);
                if (design.id) {  //错误数据直接删除
                  await this.deleteDesign(design.id);
                }
              }
            } catch (uploadError) {
              await this.updateDesignStatus(design.id, 'pending');
              console.error(`设计 ${design.id} 上传出错:`, uploadError);
            }
          }
        } else {
          console.warn('未设置上传处理函数，请使用 setUploadHandler() 设置');
        }
      }
    } catch (error) {
      console.error('检查上传设计时出错:', error);
    }
  }
  
  // 手动触发上传检查
  async triggerUploadCheck() {
    await this.checkAndUploadDesigns();
  }
  
  // 获取待上传的设计数量
  async getPendingCount() {
    try {
      const pendingDesigns = await this.getDesignsByStatus('pending');
      return pendingDesigns ? pendingDesigns.length : 0;
    } catch (error) {
      console.error('获取待上传数量出错:', error);
      return 0;
    }
  }

  base64ToFile(base64Data, filename) {
    // 去除base64前缀（如果存在）
    const arr = base64Data.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1] || base64Data) // 如果没有前缀，直接使用整个字符串
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    
    return new File([u8arr], filename, { type: mime || 'image/png' })
  }
  
  // 生成拼接图片
  async generateCollageImage(images, title) {
    return new Promise((resolve, reject) => {
      // 加载所有图片
      const imagePromises = images.map((item, index) => {
        return new Promise((resolveImg) => {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.onload = () => resolveImg({ img, title: item.title || `Image ${index + 1}` })
          img.onerror = () => resolveImg(null)
          img.src = item.imageUrl
        })
      })
      
      Promise.all(imagePromises)
        .then(loadedImages => {
          // 过滤掉加载失败的图片
          const validImages = loadedImages.filter(imgData => imgData !== null)
          if (validImages.length === 0) {
            resolve(null)
            return
          }
          
          // 固定画布大小为2000x2000
          const canvasWidth = 2000
          const canvasHeight = 2000
          const padding = 100 // 安全边距
          const spacing = 50 // 图片之间的间隔
          const titleHeight = 50 // 标题高度
          
          // 计算实际可用空间
          const availableWidth = canvasWidth - 2 * padding
          const availableHeight = canvasHeight - 2 * padding
          
          // 计算布局：每行最大图片数量
          const maxCount = validImages.length > 6 ? 3 : 2  //6张以上每行3张，6张及以下每行2张
          const maxImagesPerRow = Math.min(maxCount, validImages.length)
          const rows = Math.ceil(validImages.length / maxImagesPerRow)
          
          // 计算每张图片的最大可能宽度和高度
          const titleTopMargin = 50
          const maxImgWidth = (availableWidth - (maxImagesPerRow - 1) * spacing) / maxImagesPerRow
          const maxImgHeight = (availableHeight - (rows - 1) * (spacing + titleHeight + titleTopMargin)) / rows
          
          // 计算每张图片的实际尺寸（保持宽高比）
          const imageInfos = validImages.map(imgData => {
            const aspectRatio = imgData.img.width / imgData.img.height
            let width, height
            
            // 计算合适的尺寸
            if (maxImgWidth / maxImgHeight > aspectRatio) {
              // 高度受限
              height = maxImgHeight
              width = height * aspectRatio
            } else {
              // 宽度受限
              width = maxImgWidth
              height = width / aspectRatio
            }
            
            return {
              imgData,
              width,
              height
            }
          })
          
          // 创建画布
          const canvas = document.createElement('canvas')
          canvas.width = canvasWidth
          canvas.height = canvasHeight
          const ctx = canvas.getContext('2d')
          
          // 绘制背景
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, canvasWidth, canvasHeight)
          
          // 绘制图片和标题
          let currentY = padding
          for (let i = 0; i < rows; i++) {
            const startIndex = i * maxImagesPerRow
            const endIndex = Math.min(startIndex + maxImagesPerRow, validImages.length)
            const rowImages = imageInfos.slice(startIndex, endIndex)
            const rowImageCount = rowImages.length
            
            // 计算该行图片的起始X位置（水平居中）
            const rowTotalWidth = rowImages.reduce((total, info) => total + info.width, 0) + (rowImageCount - 1) * spacing
            const startX = padding + (availableWidth - rowTotalWidth) / 2
            
            // 计算该行的最大高度
            const rowMaxHeight = Math.max(...rowImages.map(info => info.height))
            
            // 绘制该行图片和标题
            let currentX = startX
            rowImages.forEach((imageInfo, index) => {
              const { imgData, width, height } = imageInfo
              const y = currentY + (rowMaxHeight - height) / 2 // 垂直居中
              
              // 绘制图片（保持原始宽高比）
              ctx.drawImage(imgData.img, currentX, y, width, height)
              
              // 绘制标题（加一个上边距）
              ctx.fillStyle = '#000000'
              ctx.font = 'bold 48px Arial'
              ctx.textAlign = 'center'
              // 添加上边距，使标题与图片之间有更多空间
              const titleTopMargin = 50
              ctx.fillText(imgData.title, currentX + width / 2, currentY + rowMaxHeight + titleTopMargin)
              
              currentX += width + spacing
            })
            
            currentY += rowMaxHeight + titleHeight + titleTopMargin + spacing
          }
          
          // 转换为base64
          const base64Data = canvas.toDataURL('image/png')
          resolve(base64Data)
        })
        .catch(error => {
          console.error('生成拼接图片失败:', error)
          resolve(null)
        })
    })
  }

  /**
   * 清理过期设计（无状态保护）
   * @param {number} maxAgeDays - 最大保留天数
   * @returns {Promise<number>} 删除的设计数量
   */
  async cleanupExpiredDesigns(maxAgeDays = 1) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      
      // 计算截止时间（当前时间 - maxAgeDays天）
      const cutoffTime = Date.now() - (maxAgeDays * 24 * 60 * 60 * 1000);
      const range = IDBKeyRange.upperBound(cutoffTime); // timestamp <= cutoffTime
      
      const request = index.openCursor(range);
      let deletedCount = 0;
      const deletePromises = [];
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          // 删除这条记录
          const deleteRequest = store.delete(cursor.primaryKey);
          deletePromises.push(
            new Promise((res, rej) => {
              deleteRequest.onsuccess = res;
              deleteRequest.onerror = rej;
            })
          );
          deletedCount++;
          cursor.continue();
        } else {
          // 所有匹配的记录已遍历完成
          Promise.all(deletePromises)
            .then(() => {
              console.log(`清理完成：删除了 ${deletedCount} 个超过 ${maxAgeDays} 天的设计`);
              resolve(deletedCount);
            })
            .catch(reject);
        }
      };
      
      request.onerror = (event) => {
        reject(new Error('清理过期设计失败: ' + event.target.error));
      };
    });
  }

  /**
   * 启动定期自动清理
   * @param {number} checkIntervalHours - 检查间隔（小时）
   * @param {number} maxAgeDays - 最大保留天数
   * @returns {Function} 停止清理的函数
   */
  startAutoCleanup(checkIntervalHours = 24, maxAgeDays = 1) {
    // 清理函数
    const cleanupTask = async () => {
      try {
        // console.log(`[${new Date().toLocaleTimeString()}] 开始自动清理检查...`);
        const deletedCount = await this.cleanupExpiredDesigns(maxAgeDays);
        if (deletedCount > 0) {
          // console.log(`已自动清理 ${deletedCount} 个过期设计`);
        }
      } catch (error) {
        console.error('自动清理失败:', error);
      }
    };
    
    // 立即执行一次
    cleanupTask();
    
    // 设置定时器
    const intervalMs = checkIntervalHours * 60 * 60 * 1000;
    const intervalId = setInterval(cleanupTask, intervalMs);
    
    // console.log(`已启动自动清理：每 ${checkIntervalHours} 小时检查一次，删除超过 ${maxAgeDays} 天的数据`);
    
    // 返回停止函数
    return () => {
      clearInterval(intervalId);
      // console.log('已停止自动清理');
    };
  }

  // 创建通知消息
  createNotification(obj) {
    this.uploadNotify[obj.reqId] = Notification({
      title: '提示',
      message: `${obj.proName}，设计效果图生成中，请稍后查看`,
      duration: 0,
      // offset: 100,
      position: 'bottom-right',
    })
  }

  closeNotification(reqId) {
    this.uploadNotify[reqId].close()
  }
}

// 创建并配置单例实例
const designCacheDB = new DesignCacheDB();

// 默认上传处理函数（需要根据实际项目修改）
designCacheDB.setUploadHandler(async (design, type) => {
  // 实际上传逻辑
  // console.log(`上传设计: ${design.id}`, design.data);
  let uploadNotify = null
  try {
    if (type !== 'add') {
      uploadNotify = Notification({
        title: '提示',
        message: `${design.proName}，设计效果图生成中，请稍后查看`,
        duration: 0,
        // offset: 100,
        position: 'bottom-right',
      })
    }
    designCacheDB.aliyunOssToken = null //每次保存设计前重置一下oss数据
    const localEffects = design.data.localEffects
    let form = _.clone(design.data.form)
    if (localEffects && localEffects.length > 0 && localEffects.some(v => v.render_url)) { //只要有一张效果图就执行下面逻辑
      let skuList = []
      let coverPic = ''
      for (let record of localEffects) {
        let base64Data = record.render_url
        if (!base64Data) {
          if (store.getters.dictMap['photo_merging']?.[design.data.proCode]) {
            // 绘制一张边界明显的空白图片
            const canvas = document.createElement('canvas')
            canvas.width = 2000
            canvas.height = 2000
            const ctx = canvas.getContext('2d')
            
            // 绘制白色背景
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            
            // 绘制明显的边界
            ctx.strokeStyle = '#666666'
            ctx.lineWidth = 4
            ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20)
            
            // 绘制 "无图片" 文字
            // ctx.fillStyle = '#666666'
            // ctx.font = 'bold 32px Arial'
            // ctx.textAlign = 'center'
            // ctx.fillText('无图片', canvas.width / 2, canvas.height / 2)
            
            base64Data = canvas.toDataURL('image/png')
          } else {
            continue
          }
        }
        const file = designCacheDB.base64ToFile(base64Data, new Date().getTime() + ".png")
        let result = await uploadFileNew(file, false, designCacheDB.aliyunOssToken)
        designCacheDB.aliyunOssToken = result.aliyunOssToken
        skuList.push({
          color: record.colorName,
          imageUrl: result.fileUrl,
          title: record.title,
        })
      }
      let colorList = []
      const newSkuList = []

      skuList.map((el) => {
        colorList.push(el.color)
      })

      colorList = _.uniq(colorList)

      for (const e of colorList) {
        let ob = {
          color: e,
          imageList: [],
        }

        const colorImages = []
        skuList.map((el) => {
          if (e === el.color) {
            ob.imageList.push(el.imageUrl)
            colorImages.push({
              imageUrl: el.imageUrl,
              title: el.title
            })
          }
          if (!coverPic) {
            coverPic = el.imageUrl
          }
        })
        
        // 生成拼接图片
        if (store.getters.dictMap['photo_merging']?.[design.data.proCode] && colorImages.length > 1) {
          const collageBase64 = await designCacheDB.generateCollageImage(colorImages, e)
          if (collageBase64) {
            const file = designCacheDB.base64ToFile(collageBase64, new Date().getTime() + "_collage.png")
            let result = await uploadFileNew(file, false, designCacheDB.aliyunOssToken)
            designCacheDB.aliyunOssToken = result.aliyunOssToken
            // 将拼接图片添加到imageList中
            ob.imageList.unshift(result.fileUrl)
          }
        }
        
        newSkuList.push(ob)
      }
      form.skuList = newSkuList
      form.coverPic = coverPic
    }

    // 上传前再次检查最新状态是否是未完成
    const allDesigns = await designCacheDB.getAllDesigns()
    const currentDesign = allDesigns.find(d => d.id === design.id)
    if (!currentDesign || !['pending', 'uploading'].includes(currentDesign.status)) {
      if (type !== 'add' && uploadNotify) {
        uploadNotify.close() //非新增时没有调用createNotification所以不能用closeNotification关闭
      } else if (type === 'add' && design.reqId) {
        designCacheDB.closeNotification(design.reqId)
      }
      return false
    }

    // console.log('上传设计数据: ', form)

    await addUserDesign(form)
    if (type == 'add') { //非新增时没有调用createNotification所以不能用closeNotification关闭
      designCacheDB.closeNotification(design.reqId)
    } else if (uploadNotify) {
      uploadNotify.close()
    }
    return true;
  } catch (error) {
    console.error('上传失败:', error);
    if (type == 'add') { //非新增时没有调用createNotification所以不能用closeNotification关闭
      designCacheDB.closeNotification(design.reqId)
    } else if (uploadNotify) {
      uploadNotify.close()
    }
    return false;
  }
});

// 导出单例实例
export default designCacheDB;